import {
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import { AssetTypes } from '@tassat/common-definitions';
import { AccountRecord } from '@tassat/fred-definitions';
import { ButtonType, WalletAddress } from '@tassat/text';
import { convertToNumber, formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';
import React from 'react';

import { registerTimeout } from 'Lib/serverTimeout';
import { formatEpochDate } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { pledgeCollateralTimeoutAction } from 'Reducers/actions/validationActions';
import { useAppDispatch } from 'Reducers/store/hooks';

import { ICreatePledgeFormValues } from '../overlays/createPledge/createPledgeForm/types';

interface ICreatePledgeConfirmationProps extends WithStyles<typeof muiStyles> {
    createPledgeValues: ICreatePledgeFormValues;
    currentWallet: AccountRecord;
    handleCreatePledge: (createPledgeValues: ICreatePledgeFormValues) => void;
    cancelConfirmation: () => void;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: { padding: '0 5rem' },
    contentDivider: {
        marginTop: '.5rem',
        marginBottom: '.3rem'
    },
    amountText: {
        padding: '.5rem',
        paddingBottom: '1.5rem'
    },
    disclaimerContainer: { paddingTop: '1.5rem' },
    disclaimerText: { flexBasis: '20%' },
    paymentDetailsContainer: {},
    table: {
        borderCollapse: 'separate' as const,
        borderSpacing: '.5rem'
    },
    tableRow: { marginBottom: '.3rem' },
    tableLabelCell: {
        backgroundColor: theme?.app?.applicationColors.tableRowLabelColor ||
            'rgba(185, 217, 236,5)',
        fontWeight: 'bold' as const
    },
    tableCell: {
        border: 'none' as const,
        paddingTop: '.3rem',
        paddingBottom: '.3rem'
    },
    submitContainer: { paddingTop: '2.5rem' },
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    }
});

export function CreatePledgeConfirmation(props: ICreatePledgeConfirmationProps) {
    const {
        classes,
        createPledgeValues,
        handleCreatePledge,
        cancelConfirmation
    } = props;

    const dispatch = useAppDispatch();
    const onTimeoutError = () => dispatch(pledgeCollateralTimeoutAction);

    const assetObj = createPledgeValues.AssetType?.refData?.security ||
        createPledgeValues.AssetType?.refData?.currency;

    const {
        recipientName,
        recipientAddress,
        recipientFirmName
    } = React.useMemo(() => {
        if (createPledgeValues.Recipient) {
            return {
                recipientName: createPledgeValues.Recipient.label,
                recipientAddress: createPledgeValues.Recipient.value.Account,
                recipientFirmName: createPledgeValues.Recipient.value.FirmName
            };
        } else if (createPledgeValues.RecipientAddress) {
            return {
                recipientName: createPledgeValues.RecipientAccountName,
                recipientAddress: createPledgeValues.RecipientAddress,
                recipientFirmName: createPledgeValues.RecipientFirmName
            };
        } else {
            return {
                recipientName: '',
                recipientAddress: '',
                recipientFirmName: ''
            };
        }
    }, [createPledgeValues]);

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Typography variant='body1'>You are about to post collateral for</Typography>
                <Typography
                    className={classes.amountText}
                    variant='h3'
                >{assetObj?.Type === AssetTypes.ASSET ?
                        `${formatAsAssetNoSign(convertToNumber(createPledgeValues.AssetAmount), assetObj, true)} ${assetObj?.Units} of ${assetObj?.Ticker}`
                        : formatAsAsset(createPledgeValues.FiatAmount, createPledgeValues.AssetType.refData?.currency, 'symbol')}
                </Typography>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                container
                direction='row'
                item
                spacing={0}
            >
                <Grid
                    className={classes.paymentDetailsContainer}
                    container
                    direction='column'
                    item
                    lg={6}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    <Table className={classes.table}>
                        <TableHead />
                        <TableBody>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Sender&apos;s Lynq Account Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {createPledgeValues.Sender.AccountName}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Recipient&apos;s Lynq Account Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {recipientName ?
                                        recipientName
                                        : (
                                            <WalletAddress
                                                buttonType={ButtonType.SMALL}
                                                isAdmin={false}
                                                walletAddress={recipientAddress}
                                            />
                                        )}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Recipient Account ID</TableCell>
                                <TableCell className={classes.tableCell}>
                                    <WalletAddress
                                        buttonType={ButtonType.SMALL}
                                        isAdmin={false}
                                        walletAddress={recipientAddress}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Public Description</TableCell>
                                <TableCell className={classes.tableCell}>{createPledgeValues.PublicDescription ? createPledgeValues.PublicDescription : '-'}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Private Description</TableCell>
                                <TableCell className={classes.tableCell}>{createPledgeValues.PrivateDescription ? createPledgeValues.PrivateDescription : '-'}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Timestamp</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {formatEpochDate(createPledgeValues.RequestTime)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
                {assetObj?.Type === AssetTypes.ASSET ?
                    <Grid
                        className={classes.disclaimerContainer}
                        container
                        direction='column'
                        item
                        lg={4}
                        md={12}
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Typography
                            className={classes.disclaimerText}
                            variant='body1'
                        >
                            Please review the details of your post collateral request.
                        </Typography>
                        <Typography
                            className={classes.disclaimerText}
                            variant='body1'
                        >
                            <strong>
                                All transfers following a collateral post are final and you will not
                                be able to reclaim your shares if such shares are transferred to the recipient&#39;s account.
                            </strong>
                        </Typography>
                    </Grid>
                    : <></>}
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                alignItems='center'
                className={classes.submitContainer}
                container
                direction='column'
                item
                spacing={0}
            >
                <SubmitButton
                    onClick={() => {
                        handleCreatePledge(createPledgeValues);
                        registerTimeout(onTimeoutError, 'pledgeCollateral');
                    }}
                    type='pledgeCollateral-confirm'
                />
                <Grid item>
                    <Typography
                        className={classes.cancelButton}
                        onClick={cancelConfirmation}
                        variant='h4'
                    >CANCEL
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default withStyles(muiStyles)(CreatePledgeConfirmation);